// Tiny element builder — all widget UI is plain DOM, no templates, no innerHTML
// for user/content strings (only textContent), so nothing can inject into the host.

type Props = {
  class?: string;
  text?: string;
  attrs?: Record<string, string>;
  on?: Partial<
    Record<'click' | 'keydown' | 'input' | 'submit' | 'mousedown' | 'change', (ev: any) => void>
  >;
};

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  children: (HTMLElement | SVGElement)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.class) node.className = props.class;
  if (props.text !== undefined) node.textContent = props.text;
  if (props.attrs) for (const [k, v] of Object.entries(props.attrs)) node.setAttribute(k, v);
  if (props.on)
    for (const [evt, handler] of Object.entries(props.on)) {
      // every UI handler is wrapped: a listener throwing must never surface on the host page
      node.addEventListener(evt, (ev) => {
        try {
          (handler as (e: Event) => void)(ev);
        } catch {
          /* silent by design */
        }
      });
    }
  for (const child of children) node.appendChild(child);
  return node;
}

// LLM replies arrive as plain text: bare URLs (not clickable) and source
// markers like "[1]" the visitor can't resolve. Strip the markers and turn
// URLs into real links — still no innerHTML, links are built as elements with
// textContent, so the host page stays uninjectable.
const CITATION_RE = /[ \t]*\[\d+\]/g;
const URL_RE = /https?:\/\/[^\s<]+/gi;
// trailing chars that are almost always sentence punctuation, not part of the URL
const URL_TRAIL_RE = /[.,;:!?)\]}'"«»]+$/;

export function richTextNodes(text: string): (Text | HTMLElement)[] {
  const clean = text.replace(CITATION_RE, '');
  const nodes: (Text | HTMLElement)[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(clean))) {
    let url = m[0];
    const trail = url.match(URL_TRAIL_RE);
    if (trail) url = url.slice(0, url.length - trail[0].length);
    if (!url) continue;
    if (m.index > last) nodes.push(document.createTextNode(clean.slice(last, m.index)));
    nodes.push(
      el('a', {
        class: 'cb-link',
        text: url,
        attrs: { href: url, target: '_blank', rel: 'noopener noreferrer' },
      }),
    );
    last = m.index + url.length;
  }
  if (last < clean.length) nodes.push(document.createTextNode(clean.slice(last)));
  return nodes;
}

/** a message-content <span> with citations stripped and URLs linkified */
export function richTextSpan(text: string): HTMLElement {
  const span = el('span');
  for (const node of richTextNodes(text)) span.appendChild(node);
  return span;
}

/** inline SVG icons (stroke = currentColor so they follow the button color) */
export function svgIcon(path: string, size = 20): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', path);
  svg.appendChild(p);
  return svg;
}

export const ICONS = {
  chat: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z',
  close: 'M18 6 6 18 M6 6l12 12',
  send: 'M22 2 11 13 M22 2 15 22l-4-9-9-4 20-7z',
  star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  check: 'M20 6 9 17l-5-5',
  pin: 'M9 4h6l-1 7 3 3H7l3-3z M12 14v7',
  calendar: 'M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M16 2v4 M8 2v4 M3 10h18',
  chevronLeft: 'M15 18l-6-6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
};
